import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, type Idea, type IdeaFilter, type IdeaPageFilter, type Product } from "@/api";

export const ideaKeys = {
	all: ["ideas"] as const,
	list: (filter: IdeaFilter) => ["ideas", "list", filter] as const,
	page: (filter: IdeaPageFilter) => ["ideas", "page", filter] as const,
	counts: (shopId: string) => ["ideas", "counts", shopId] as const,
	shops: ["ideas", "shops"] as const,
	pinProducts: (ideaId: string) => ["ideas", "pinProducts", ideaId] as const,
};

export function useIdeas(filter: IdeaFilter) {
	return useQuery({
		queryKey: ideaKeys.list(filter),
		queryFn: () => api.ideas.list(filter),
		// Keep the previous list on screen while a status-filter switch loads.
		placeholderData: keepPreviousData,
	});
}

/** Страница списка идей с сервера. */
export function useIdeaPage(filter: IdeaPageFilter) {
	return useQuery({
		queryKey: ideaKeys.page(filter),
		queryFn: () => api.ideas.page(filter),
		placeholderData: keepPreviousData,
	});
}

export function useIdeaCounts(shopId: string) {
	return useQuery({
		queryKey: ideaKeys.counts(shopId),
		queryFn: () => api.ideas.counts(shopId || undefined),
		placeholderData: keepPreviousData,
	});
}

export function useIdeaShops() {
	return useQuery({ queryKey: ideaKeys.shops, queryFn: () => api.ideas.shops() });
}

// Products referenced by an idea's shoppable pins, resolved by id. Unresolvable
// products are silently skipped — the dialog falls back to the raw product id.
export function useIdeaPinProducts(idea: Idea | null) {
	const ids = idea
		? [...new Set(idea.images.flatMap((img) => img.pins.map((pin) => pin.productId)))]
		: [];
	return useQuery({
		queryKey: ideaKeys.pinProducts(idea?.id ?? ""),
		queryFn: async () => {
			const entries = await Promise.all(
				ids.map((id) =>
					api.products
						.get(id)
						.then((p) => [id, p] as const)
						.catch(() => null),
				),
			);
			return Object.fromEntries(
				entries.filter((e): e is readonly [string, Product] => e !== null),
			);
		},
		enabled: !!idea && ids.length > 0,
	});
}
